import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Border_Shorthand = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">✍️ CSS Border Shorthand</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Instead of writing <code>border-width</code>, <code>border-style</code>,
        and <code>border-color</code> separately, CSS allows you to combine them
        into a single <code>border</code> shorthand property. This makes your
        code shorter and easier to read.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock code={`selector { border: width style color; }`} />
        <p>
          The order is always: <strong>width → style → color</strong>
        </p>
      </div>

      {/* Basic Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">Basic Examples</h2>
        <CodeCopyBlock
          code={`p { border: 2px solid red; }
p { border: 5px dashed blue; }
p { border: thick dotted green; }`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap",
            marginTop: "1rem",
          }}
        >
          <div style={{ border: "2px solid red", padding: "10px" }}>
            2px solid red
          </div>
          <div style={{ border: "5px dashed blue", padding: "10px" }}>
            5px dashed blue
          </div>
          <div style={{ border: "thick dotted green", padding: "10px" }}>
            thick dotted green
          </div>
        </div>
      </div>

      {/* Individual Side Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">Borders on Individual Sides</h2>
        <p>
          You can also use shorthand for a specific side with{" "}
          <code>border-top</code>, <code>border-right</code>,{" "}
          <code>border-bottom</code>, and <code>border-left</code>.
        </p>
        <CodeCopyBlock
          code={`div { border-top: 4px solid red; }
div { border-right: 4px dashed blue; }
div { border-bottom: 4px dotted green; }
div { border-left: 4px double orange; }`}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            marginTop: "1rem",
          }}
        >
          <div style={{ borderTop: "4px solid red", padding: "10px" }}>
            Top border
          </div>
          <div style={{ borderRight: "4px dashed blue", padding: "10px" }}>
            Right border
          </div>
          <div style={{ borderBottom: "4px dotted green", padding: "10px" }}>
            Bottom border
          </div>
          <div style={{ borderLeft: "4px double orange", padding: "10px" }}>
            Left border
          </div>
        </div>
      </div>

      {/* Mixing Width, Style, Color */}
      <div className="tutorial-section">
        <h2 className="section-title">Mixing Width, Style & Color</h2>
        <p>
          You can combine all three in one shorthand. You can also use keywords
          like <code>thin</code>, <code>medium</code>, or <code>thick</code> for
          width.
        </p>
        <CodeCopyBlock
          code={`div { border: thin solid black; }
div { border: medium dashed orange; }
div { border: thick double purple; }`}
        />
        <div
          style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap",
            marginTop: "1rem",
          }}
        >
          <div style={{ border: "thin solid black", padding: "10px" }}>
            thin solid black
          </div>
          <div style={{ border: "medium dashed orange", padding: "10px" }}>
            medium dashed orange
          </div>
          <div style={{ border: "thick double purple", padding: "10px" }}>
            thick double purple
          </div>
        </div>
      </div>

      {/* Transparent Borders */}
      <div className="tutorial-section">
        <h2 className="section-title">Transparent Borders</h2>
        <p>
          You can use the <code>transparent</code> keyword to create invisible
          borders while still keeping layout spacing.
        </p>
        <CodeCopyBlock
          code={`div {
  border: 5px solid transparent;
}`}
        />
        <div
          style={{
            border: "5px solid transparent",
            backgroundColor: "#f0f0f0",
            padding: "15px",
            marginTop: "0.5rem",
          }}
        >
          🚫 Transparent border (space preserved)
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          After shorthand, the next important topic is{" "}
          <a href="/css/border-radius" className="learn-more">
            CSS Border Radius
          </a>{" "}
          to create rounded corners.
        </p>
      </div>
    </div>
  );
};

export default Border_Shorthand;
